import { Controller } from 'stimulus'
import { DocumentLibraryPaging } from '../tiptap/document_library_paging'

export default class extends Controller {
  static targets = ['form', 'results', 'feedback', 'error', 'collection', 'collectionButton', 'viewButton', 'advanced', 'moreFilters']

  connect() {
    this.pendingPdfSaves = new Map()
    let view = 'cards'
    try { view = localStorage.getItem('writingio.documents.view') || view } catch (_) {}
    this.setView(view)
    this.paging = new DocumentLibraryPaging(this.element)
    this.syncCollection()
    this.onPageShow = event => {
      if (event.persisted && this.element.dataset.documentsLibraryRefreshPending === 'true') this.submit()
    }
    window.addEventListener('pageshow', this.onPageShow)
    if (this.element.dataset.documentsLibraryRefreshPending === 'true' || this.resultsTarget.querySelector('[data-library-view]')?.dataset.libraryView !== this.element.dataset.view) this.submit()
    else this.paging.refresh()
  }

  disconnect() {
    this.paging.stop()
    this.pendingPdfSaves.forEach(listener => document.removeEventListener('turbo:submit-end', listener))
    this.pendingPdfSaves.clear()
    window.removeEventListener('pageshow', this.onPageShow)
    clearTimeout(this.searchTimer)
    if (this.request) this.request.abort()
  }

  chooseView(event) {
    const view = event.currentTarget.dataset.view
    if (view === this.element.dataset.view) return
    this.setView(view)
    try { localStorage.setItem('writingio.documents.view', view) } catch (_) {}
    this.submit()
  }

  setView(view) {
    this.element.dataset.view = view === 'list' ? 'list' : 'cards'
    this.formTarget.elements.library_view.value = this.element.dataset.view
    this.viewButtonTargets.forEach(button => button.setAttribute('aria-pressed', String(button.dataset.view === this.element.dataset.view)))
  }

  chooseCollection(event) {
    this.collectionTarget.value = event.currentTarget.dataset.collection
    if (this.collectionTarget.value === 'recent') this.formTarget.elements['filterrific[sorted_by]'].value = 'updated_at_desc'
    this.syncCollection()
    this.submit()
  }

  syncCollection() {
    const recent = this.formTarget.elements['filterrific[sorted_by]'].value === 'updated_at_desc'
    this.collectionButtonTargets.forEach(button => {
      const selected = button.dataset.collection === this.collectionTarget.value && (button.dataset.collection !== 'recent' || recent)
      button.setAttribute('aria-pressed', String(selected))
    })
  }

  toggleFilters() {
    const expanded = this.advancedTarget.hidden
    this.advancedTarget.hidden = !expanded
    this.moreFiltersTarget.setAttribute('aria-expanded', String(expanded))
    if (expanded) {
      const headerBottom = this.element.querySelector('#publish-nav').getBoundingClientRect().bottom
      const top = window.scrollY + this.advancedTarget.getBoundingClientRect().top - headerBottom - 16
      const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
      window.scrollTo({ top: Math.max(0, top), behavior })
      this.advancedTarget.querySelector('select, input')?.focus({ preventScroll: true })
    }
  }

  pdfSaveStarted(event) {
    const form = event.target
    if (this.pendingPdfSaves.has(form)) return
    const entryId = form.closest('turbo-frame')?.querySelector('[id^="post_"]')?.id
    const finished = event => {
      if (event.detail.formSubmission.formElement !== form) return
      document.removeEventListener('turbo:submit-end', finished)
      this.pendingPdfSaves.delete(form)
      if (!event.detail.success || !this.element.isConnected) return
      this.returnFocusId = entryId?.replace(/_container$/, '')
      this.submit()
    }
    this.pendingPdfSaves.set(form, finished)
    document.addEventListener('turbo:submit-end', finished)
  }

  hideFilters() {
    this.advancedTarget.hidden = true
    this.moreFiltersTarget.setAttribute('aria-expanded', 'false')
    this.moreFiltersTarget.focus({ preventScroll: true })
  }

  scheduleSearch(event) {
    if (event.target.name !== 'filterrific[search_query]') return
    this.paging.stop()
    this.element.dataset.documentsLibraryRefreshPending = 'true'
    clearTimeout(this.searchTimer)
    if (this.request) this.request.abort()
    this.searchTimer = setTimeout(() => this.submit(), 300)
  }

  changeFilter(event) {
    if (event.target.name === 'filterrific[search_query]') return
    this.syncCollection()
    this.submit()
  }

  resetFilters(event) {
    event.preventDefault()
    Array.from(this.formTarget.elements).forEach(input => {
      if (!input.name.startsWith('filterrific[')) return
      if (input.type === 'checkbox') input.checked = false
      else if (input.name === 'filterrific[show_trash]') input.value = 'false'
      else input.value = ''
    })
    this.formTarget.elements['filterrific[sorted_by]'].value = 'updated_at_desc'
    this.syncCollection()
    this.submit()
    this.formTarget.elements['filterrific[search_query]'].focus()
  }

  refresh(event) {
    this.returnFocusId = event.target.closest('.document-library-entry')?.id
    this.submit()
  }

  actionFailed() {
    this.feedbackTarget.classList.remove('sr-only')
    this.feedbackTarget.textContent = 'The document could not be updated. Please try the action again.'
  }

  retry() { this.submit() }

  loadMore() { this.paging.load() }

  coverFailed(event) {
    event.currentTarget.hidden = true
    event.currentTarget.closest('.document-library-entry').classList.add('document-library-without-cover')
  }

  submit(event) {
    if (event) event.preventDefault()
    this.paging.stop()
    clearTimeout(this.searchTimer)
    if (this.request) this.request.abort()
    this.element.dataset.documentsLibraryRefreshPending = 'true'
    this.errorTarget.hidden = true
    this.feedbackTarget.classList.remove('sr-only')
    this.feedbackTarget.textContent = 'Updating documents…'
    this.resultsTarget.setAttribute('aria-busy', 'true')
    this.request = window.jQuery.ajax({
      url: this.formTarget.action,
      data: window.jQuery(this.formTarget).serialize(),
      type: 'GET',
      dataType: 'script'
    }).done(() => {
      this.paging.refresh()
      delete this.element.dataset.documentsLibraryRefreshPending
      this.feedbackTarget.classList.add('sr-only')
      this.feedbackTarget.textContent = `${this.resultsTarget.querySelector('.document-library-count')?.textContent.trim() || 'Documents updated'}`
      if (this.returnFocusId) {
        this.element.querySelector(`#${this.returnFocusId} .document-library-options`)?.focus()
        this.returnFocusId = null
      }
    }).fail((_xhr, status) => {
      if (status === 'abort') return
      this.feedbackTarget.textContent = ''
      this.errorTarget.hidden = false
    }).always(() => {
      this.resultsTarget.removeAttribute('aria-busy')
      this.request = null
    })
  }
}
